import axios from "axios";
import { createContext } from 'preact';
import { useEffect, useState, useRef, useCallback, useContext } from 'preact/hooks';
import { Loader } from '../elements';

// Environment detection
const isDevEnvironment = !import.meta.env.PROD;

/**
 * AppConfigContext provides application configuration including colors and settings
 * This context is populated BEFORE the user session context to ensure
 * the application has the necessary configuration before rendering begins
 */
export const AppConfigContext = createContext({
  appConfig: null,
  isLoading: true,
  error: null,
  refreshConfig: () => {},
});

/**
 * Custom hook to access app configuration
 */
export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

/**
 * Hook to fetch application configuration
 * This hook manages the lifecycle of fetching app config from the backend
 */
export const useAppConfiguration = (roleCode) => {
  const [appConfig, setAppConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs para control de vida y reintentos
  const isMountedRef = useRef(true);
  const retryRef = useRef(0);
  const maxRetries = 3;

  // Build endpoint URL based on environment
  const buildEndpointUrl = useCallback((roleCode) => {
    // roleCode here is actually just used as context, the appCode is quoter.
    const ssoBase = import.meta.env.VITE_SSO_BASE_URL || 'https://sso.aguayodevs.com';
    return `${ssoBase}/api/public/bootstrap?appCode=quoter`;
  }, []);

  const fetchAppConfig = useCallback(async (roleCode) => {
    if (!roleCode) {
      setError(new Error('Role code is required to fetch app configuration'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const endpointUrl = buildEndpointUrl(roleCode);
      //console.log(`[AppConfig] Fetching configuration from: ${endpointUrl}`);

      const response = await axios.get(endpointUrl, {
        timeout: 10000, // 10 second timeout
        validateStatus: (status) => status < 500, // Don't throw for 4xx errors
      });

      if (!isMountedRef.current) return;

      // SSO bootstrap response doesn't wrap in {ok: true, data: {}}, it just returns the config
      if (response.data && response.data.app) {
        const configData = response.data;
        // Resolve logoUrl
        if (configData.branding && configData.branding.logoUrl) {
          if (configData.branding.logoUrl.startsWith('/')) {
            const ssoBase = import.meta.env.VITE_SSO_BASE_URL || 'https://sso.aguayodevs.com';
            configData.branding.logoUrl = `${ssoBase}${configData.branding.logoUrl}`;
          }
        }
        setAppConfig(configData);
        retryRef.current = 0; // Reset retry counter on success
      } else if (response.data?.ok) {
        // Fallback for legacy local structure just in case
        const configData = response.data.data;
        if (configData.branding && configData.branding.logoUrl) {
          if (configData.branding.logoUrl.startsWith('/')) {
            const ssoBase = import.meta.env.VITE_SSO_BASE_URL || 'https://sso.aguayodevs.com';
            configData.branding.logoUrl = `${ssoBase}${configData.branding.logoUrl}`;
          }
        }
        setAppConfig(configData);
        retryRef.current = 0;
      } else {
        const errorMessage = response.data?.error || `HTTP ${response.status}: Failed to load app configuration`;
        throw new Error(errorMessage);
      }

    } catch (err) {
      if (!isMountedRef.current) return;

      //console.error('[AppConfig] ❌ Error fetching app configuration:', err.message);

      // Retry logic for network errors only
      if (retryRef.current < maxRetries && 
          (axios.isAxiosError(err) && (!err.response || err.response.status >= 500))) {
        retryRef.current += 1;
        const delay = 1000 * retryRef.current; // Exponential backoff
        
        //console.log(`[AppConfig] 🔄 Retrying in ${delay}ms (attempt ${retryRef.current}/${maxRetries})`);
        
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchAppConfig(roleCode);
          }
        }, delay);
        return;
      }

      setError(err);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [buildEndpointUrl]);

  const refreshConfig = useCallback(() => {
    if (roleCode) {
      retryRef.current = 0; // Reset retry counter
      fetchAppConfig(roleCode);
    }
  }, [roleCode, fetchAppConfig]);

  // Fetch config on mount or when roleCode changes
  useEffect(() => {
    isMountedRef.current = true;
    if (roleCode) {
      fetchAppConfig(roleCode);
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [roleCode, fetchAppConfig]);

  return {
    appConfig,
    isLoading,
    error,
    refreshConfig,
  };
};

/**
 * AppConfigProvider component
 * This provider must be placed ABOVE UserSessionProvider to ensure
 * app configuration is loaded before user session establishment
 */
export const AppConfigProvider = ({ 
  roleCode, 
  children,
  fallbackLoader = null 
}) => {
  const appConfigState = useAppConfiguration(roleCode);

  // Show loading state until configuration is loaded
  if (appConfigState.isLoading) {
    return fallbackLoader || (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#1a1a1a', // Neutral dark background
        color: '#ffffff'
      }}>
        <Loader 
          loading={true} 
          label="Cargando configuración de la aplicación..." 
        />
      </div>
    );
  }

  // Show error state if configuration failed to load
  if (appConfigState.error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ff6b6b', marginBottom: '16px' }}>
          Error al cargar la configuración
        </h2>
        <p style={{ marginBottom: '24px', maxWidth: '500px' }}>
          No se pudo cargar la configuración de la aplicación. 
          Esto es necesario para continuar.
        </p>
        <p style={{ 
          fontSize: '14px', 
          color: '#aaa', 
          marginBottom: '24px',
          fontFamily: 'monospace'
        }}>
          {appConfigState.error.message}
        </p>
        <button 
          onClick={appConfigState.refreshConfig}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Render children with app config context once loaded
  return (
    <AppConfigContext.Provider value={appConfigState}>
      {children}
    </AppConfigContext.Provider>
  );
};